<template>
    <div class="row">
        <go-lotus-side-menu
            :side-menu-items="menuItems"
            @set-parent-prop="propSetFromChild($event)"
            :current-component="component"
        ></go-lotus-side-menu>

        <div class="col-12 col-md-10 main-content position-relative">
            <transition name="simple-fade" mode="out-in">
                <component
                    :is="component"
                    :key="component"
                    :user="user"
                    :current-user-therapist="currentUserTherapist"
                    :organization="organization"
                    :centers="centers"
                    :appointment-types="appointmentTypes"
                    :appointment-templates="appointmentTemplates"
                    :therapists="organizationTherapists"
                    :direct-reports="directReports"
                    :therapist-patient-relations="therapistPatientRelations"
                    :schedule-owner-prop="listProperty.owner"
                    :schedule-owner-list="list.owners"
                    :participants-list-prop="listProperty.participants"
                    :participants-list="list.participants"
                    :places-of-service="placesOfService"
                    @set-parent-prop="propSetFromChild($event)"
                ></component>
            </transition>
        </div>
    </div>
</template>

<script>
import GoLotusSideMenu from "./GoLotusSideMenu";
import SchedulePatient from "./schedule/ScheduleCalendar"; // The side menu reflects the selected component based on the import name
import ScheduleStaff from "./schedule/ScheduleCalendar"; // even though they are the same component
import ScheduleManagement from "./schedule/ScheduleManagement";
import ScheduleReplacement from "./schedule/ScheduleReplacement";

import { sideMenuItems } from "../plugins/sideMenuItems";
import { permissions } from "../plugins/permissions";

export default {
    name: "Schedule",
    props: [
        "user",
        "organization",
        "centers",
        "currentUserTherapist",
        "appointmentTemplates",
        "appointmentTypes",
        "organizationTherapists",
        "placesOfService",
    ],
    components: {
        GoLotusSideMenu,
        ScheduleStaff,
        SchedulePatient,
        ScheduleManagement,
        ScheduleReplacement,
    },
    mixins: [sideMenuItems, permissions],
    data() {
        return {
            component: "schedule-staff",
            allOrganizationPatients: [],
            userOrganizationPatients: [],
            therapistPatientRelations: [],
        };
    },
    created() {
        this.$bus.$on("set-schedule-component", payload => {
            this.component = payload;
        });
    },
    computed: {
        listProperty() {
            // The name of the property we'll use to populate the lists: ['therapist', 'patient']
            let listProperty = {
                // Default case: `this.component = 'schedule-staff'`
                owner: "therapist",
                participants: "patient",
            };
            switch (this.component) {
                case "schedule-staff":
                    break;
                case "schedule-patient":
                    listProperty.owner = "patient";
                    listProperty.participants = "therapist";
                    break;
            }
            return listProperty;
        },

        list() {
            // list.owners : Lists whose schedules can be selected from the dropdown
            // list.participants: Lists who can be selected when creating a new appointment
            let availablePatients = [];
            /* Possible outcomes:
             *   A) only the patients assigned to the user
             *   B) all the organization's patients
             * */
            if (this.canViewAllClients) {
                // Case B
                availablePatients = this.allOrganizationPatients;
            } else {
                // Case A
                availablePatients = this.userOrganizationPatients;
            }

            let availableTherapists = [];
            /* Possible outcomes:
             *   A) only themselves
             *   B) themselves + direct reports
             *   C) all the organization's therapists
             * */

            if (this.canEditScheduleStaffAll || this.canViewAllStaff) {
                // Case C
                availableTherapists = this.organizationTherapists;
            } else if (this.canEditScheduleStaffDirect) {
                // Case B
                availableTherapists = this.directReports;
            } else {
                // Case A
                availableTherapists = [this.currentUserTherapist];
            }

            let list = {
                // Default case: `this.component = 'schedule-staff'`
                owners: availableTherapists,
                participants: availablePatients,
            };
            switch (this.component) {
                case "schedule-staff":
                    break;
                case "schedule-patient":
                    list.owners = availablePatients;
                    list.participants = availableTherapists;
                    break;
            }
            return list;
        },

        directReports() {
            if (this.organizationTherapists && this.organizationTherapists.length) {
                return this.organizationTherapists.filter(
                    therapist =>
                        _.get(therapist, "user.id", false) === this.user.id ||
                        _.get(therapist, "supervisor_user.id", false) === this.user.id
                );
            }
            return [];
        },
    },
    watch: {
        organization() {
            if (this.organization) {
                this.fetchUserOrgClients();
                if (this.canViewAllClients) {
                    this.fetchOrganizationPatients();
                }
                this.fetchTherapistPatientRelations();
            }
        },

        menuBaseItems() {
            const menuEntry = this.menuBaseItems.find(m => m.title === "Schedule");
            if (menuEntry) {
                if (!menuEntry.hasOwnProperty("items")) {
                    this.populateSubMenu(menuEntry);
                }
            }
        },
    },
    methods: {
        populateSubMenu(menuEntry) {
            const index = this.menuBaseItems.indexOf(menuEntry);
            if (this.$env === "demo") {
                menuEntry.items = [
                    { title: "Staff Schedule", component: "schedule-staff" },
                    { title: "Staff Time Management", component: "schedule-management" },
                    { title: "Replacement List", component: "schedule-replacement" },
                    { title: "Clients Schedule", component: "schedule-patient" },
                ];
            } else {
                menuEntry.items = [
                    { title: "Staff Schedule", component: "schedule-staff" },
                    { title: "Clients Schedule", component: "schedule-patient" },
                ];
            }
            this.$set(this.menuBaseItems, index, menuEntry);
        },

        fetchTherapistPatientRelations() {
            axios
                .get(`/api/organizations/${this.organization.id}/patient_therapist_mappings`)
                .then(r => {
                    this.therapistPatientRelations = r.data.data;
                });
        },

        fetchOrganizationPatients() {
            axios
                .get(`/api/organizations/${this.organization.id}/patients?with=contracts`)
                .then(r => {
                    this.allOrganizationPatients = r.data.data;
                })
                .catch(error => {
                    console.error(error);
                });
        },

        fetchUserOrgClients() {
            axios
                .get(`/api/patients?organization_id=${this.organization.id}&with=contracts`)
                .then(r => {
                    this.userOrganizationPatients = r.data.data;
                })
                .catch(error => {
                    console.error(error);
                });
        },
    },
};
</script>
